import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Wrench, MoreVertical, X, Loader2, Edit3, Trash2, MessageSquare, Send, ImageIcon } from 'lucide-react';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatters';
import EmptyState from '../components/EmptyState';
import type { MaintenanceTicket } from '../context/types';
import { createNotification } from '../utils/notifications';

// ─── Portal dropdown ────────────────────────────────────────────────────────

interface DropdownPos { top: number; left: number; openUpward: boolean; }

interface TicketMenuProps {
    ticket: MaintenanceTicket;
    pos: DropdownPos;
    onEdit: () => void;
    onStatus: (id: string, s: 'new' | 'in_progress' | 'resolved') => void;
    onDelete: (id: string) => void;
    onClose: () => void;
}

function TicketMenu({ ticket, pos, onEdit, onStatus, onDelete, onClose }: TicketMenuProps) {
    const ref = useRef<HTMLDivElement>(null);

    // click-outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const style: React.CSSProperties = {
        position: 'fixed',
        left: pos.left,
        zIndex: 9999,
        minWidth: 220,
        borderRadius: 18,
        padding: 8,
        boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18)',
        border: '1px solid #e2e8f0',
        background: '#fff',
        textAlign: 'left',
        animation: 'ticketMenuIn 0.12s ease-out',
        ...(pos.openUpward
            ? { bottom: `calc(100vh - ${pos.top}px)` }
            : { top: pos.top }),
    };

    const itemStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%', padding: '11px 14px', background: 'none',
        border: 'none', borderRadius: 12, cursor: 'pointer',
        fontWeight: 600, fontSize: '0.875rem', color: '#1e293b',
        textAlign: 'left', transition: 'background 0.12s',
    };

    return createPortal(
        <>
            <style>{`
                @keyframes ticketMenuIn {
                    from { opacity: 0; transform: scale(0.96) translateY(4px); }
                    to   { opacity: 1; transform: scale(1)    translateY(0);    }
                }
            `}</style>
            <div ref={ref} style={style}>
                {/* ── Ticket Management ── */}
                <div style={{ padding: '8px 14px 4px', fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Ticket Management
                </div>
                <button
                    style={itemStyle}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    onClick={() => { onEdit(); onClose(); }}
                >
                    <span style={{ background: '#f8fafc', padding: 6, borderRadius: 8, display: 'flex' }}><Edit3 size={14} /></span>
                    Edit Details
                </button>

                <div style={{ height: 1, background: '#f1f5f9', margin: '6px 0' }} />

                {/* ── Update Status ── */}
                <div style={{ padding: '8px 14px 4px', fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Update Status
                </div>
                {ticket.status !== 'new' && (
                    <button
                        style={{ ...itemStyle, color: '#2563eb' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(59,130,246,0.06)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        onClick={() => { onStatus(ticket.id, 'new'); onClose(); }}
                    >
                        Move to Open
                    </button>
                )}
                {ticket.status !== 'in_progress' && (
                    <button
                        style={{ ...itemStyle, color: '#d97706' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(245,158,11,0.06)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        onClick={() => { onStatus(ticket.id, 'in_progress'); onClose(); }}
                    >
                        Set In Progress
                    </button>
                )}
                {ticket.status !== 'resolved' && (
                    <button
                        style={{ ...itemStyle, color: '#16a34a' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(34,197,94,0.06)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        onClick={() => { onStatus(ticket.id, 'resolved'); onClose(); }}
                    >
                        Mark as Resolved
                    </button>
                )}

                <div style={{ height: 1, background: '#f1f5f9', margin: '6px 0' }} />

                {/* ── Delete ── */}
                <button
                    style={{ ...itemStyle, color: '#ef4444', background: 'rgba(239,68,68,0.03)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.03)')}
                    onClick={() => { onDelete(ticket.id); onClose(); }}
                >
                    <span style={{ background: 'rgba(239,68,68,0.08)', padding: 6, borderRadius: 8, display: 'flex' }}><Trash2 size={14} /></span>
                    Delete Ticket
                </button>
            </div>
        </>,
        document.body
    );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function MaintenancePage() {
    const { user } = useAppContext();
    const [tickets, setTickets] = useState<MaintenanceTicket[]>([]);
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingTicket, setEditingTicket] = useState<MaintenanceTicket | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('all');

    const [detailTicket, setDetailTicket] = useState<MaintenanceTicket | null>(null);
    const [comments, setComments] = useState<any[]>([]);
    const [commentText, setCommentText] = useState('');
    const [loadingComments, setLoadingComments] = useState(false);
    const [submittingComment, setSubmittingComment] = useState(false);
    const [ticketImages, setTicketImages] = useState<{ id: string; storage_path: string }[]>([]);
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

    // Portal dropdown state
    const [openMenuTicketId, setOpenMenuTicketId] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState<DropdownPos>({ top: 0, left: 0, openUpward: false });

    // Form State
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [propertyId, setPropertyId] = useState('');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
    const [status, setStatus] = useState<'new' | 'in_progress' | 'resolved'>('new');

    const fetchMaintenanceData = async () => {
        if (!user.id) return;
        try {
            setLoading(true);
            const { data: props } = await supabase
                .from('properties')
                .select('id, name')
                .eq('owner_id', user.id);
            setProperties(props || []);
            const propertyIds = (props || []).map(p => p.id);
            if (propertyIds.length === 0) { setTickets([]); setLoading(false); return; }
            const { data: tix, error } = await supabase
                .from('maintenance_tickets')
                .select('*')
                .in('property_id', propertyIds)
                .order('created_at', { ascending: false });
            if (error) { console.error('Error fetching tickets:', error); return; }

            // Auto-advance stale tickets
            const now = Date.now();
            const TWO_DAYS  = 2  * 24 * 60 * 60 * 1000;
            const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
            const toProgress = (tix || []).filter(t => t.status === 'new'         && now - new Date(t.updated_at || t.created_at).getTime() >= TWO_DAYS);
            const toResolved = (tix || []).filter(t => t.status === 'in_progress' && now - new Date(t.updated_at || t.created_at).getTime() >= TWO_WEEKS);
            await Promise.all([
                ...toProgress.map(t => supabase.from('maintenance_tickets').update({ status: 'in_progress', updated_at: new Date().toISOString() }).eq('id', t.id)),
                ...toResolved.map(t => supabase.from('maintenance_tickets').update({ status: 'resolved',    updated_at: new Date().toISOString() }).eq('id', t.id)),
            ]);
            // Re-fetch if any tickets were auto-advanced
            if (toProgress.length + toResolved.length > 0) {
                const { data: updated } = await supabase
                    .from('maintenance_tickets').select('*').in('property_id', propertyIds).order('created_at', { ascending: false });
                setTickets(updated || []);
            } else {
                setTickets(tix || []);
            }
        } catch (err) {
            console.error('Unexpected error in MaintenancePage:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadComments = async (ticketId: string) => {
        setLoadingComments(true);
        const { data } = await supabase
            .from('ticket_comments')
            .select('*')
            .eq('ticket_id', ticketId)
            .order('created_at', { ascending: true });
        setComments(data || []);
        setLoadingComments(false);
    };

    const loadTicketImages = async (ticketId: string) => {
        const { data } = await supabase
            .from('ticket_images')
            .select('id, storage_path')
            .eq('ticket_id', ticketId)
            .order('created_at', { ascending: true });
        setTicketImages(data || []);
    };

    const openDetailDrawer = (ticket: MaintenanceTicket) => {
        setDetailTicket(ticket);
        setCommentText('');
        setTicketImages([]);
        loadComments(ticket.id);
        loadTicketImages(ticket.id);
    };

    const handleSubmitComment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!commentText.trim() || !detailTicket || !user.id) return;
        setSubmittingComment(true);
        const { error } = await supabase.from('ticket_comments').insert({
            ticket_id: detailTicket.id,
            author_id: user.id,
            author_name: user.name || user.email || 'Landlord',
            content: commentText.trim(),
        });
        if (!error) {
            setCommentText('');
            loadComments(detailTicket.id);
        }
        setSubmittingComment(false);
    };

    useEffect(() => { fetchMaintenanceData(); }, [user.id]);

    const handleStatusChange = async (id: string, s: 'new' | 'in_progress' | 'resolved') => {
        const { error } = await supabase
            .from('maintenance_tickets')
            .update({ status: s, updated_at: new Date().toISOString() })
            .eq('id', id);
        if (error) { alert('Failed to update status.'); return; }

        // Notify the tenant who created the ticket
        const ticket = tickets.find(t => t.id === id);
        if (ticket?.created_by) {
            const label = s === 'new' ? 'reopened' : s === 'in_progress' ? 'now in progress' : 'resolved';
            await createNotification(
                ticket.created_by,
                'ticket_status',
                'Maintenance Update',
                `Your request "${ticket.title}" is ${label}.`,
                '/app/tenant/maintenance'
            );
        }

        fetchMaintenanceData();
    };

    const handleDeleteTicket = async (id: string) => {
        if (!confirm('Are you sure you want to delete this ticket?')) return;
        const { error } = await supabase.from('maintenance_tickets').delete().eq('id', id);
        if (error) alert('Error deleting ticket: ' + error.message);
        else fetchMaintenanceData();
    };

    const handleCreateOrUpdateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !propertyId || !user.id) return;
        setIsSubmitting(true);
        try {
            if (editingTicket) {
                const { error } = await supabase
                    .from('maintenance_tickets')
                    .update({ title, description, property_id: propertyId, priority, status, updated_at: new Date().toISOString() })
                    .eq('id', editingTicket.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('maintenance_tickets')
                    .insert({ title, description, property_id: propertyId, created_by: user.id, status: 'new', priority, created_at: new Date().toISOString() });
                if (error) throw error;
            }
            setIsAddModalOpen(false);
            setEditingTicket(null);
            setTitle(''); setDescription(''); setPropertyId(''); setPriority('medium'); setStatus('new');
            fetchMaintenanceData();
        } catch (err: any) {
            alert('Error: ' + err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openEditModal = (ticket: MaintenanceTicket) => {
        setEditingTicket(ticket);
        setTitle(ticket.title);
        setDescription(ticket.description || '');
        setPropertyId(ticket.property_id);
        setPriority(ticket.priority as any);
        setStatus(ticket.status as any);
        setIsAddModalOpen(true);
    };

    // Open the portal menu anchored to the trigger button
    const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, ticketId: string) => {
        e.stopPropagation();
        if (openMenuTicketId === ticketId) { setOpenMenuTicketId(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        const MENU_HEIGHT = 280; // approximate height
        const MENU_WIDTH = 220;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < MENU_HEIGHT && rect.top > MENU_HEIGHT;
        setMenuPos({
            top: openUpward ? rect.top : rect.bottom + 6,
            left: Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
            openUpward,
        });
        setOpenMenuTicketId(ticketId);
    };

    const openTicketsCount = tickets.filter(t => t.status !== 'resolved').length;
    const filteredTickets = tickets.filter(t => filterStatus === 'all' || t.status === filterStatus);
    const activeTicket = tickets.find(t => t.id === openMenuTicketId) ?? null;

    return (
        <div className="page dashboard-page">
            <div className="page__header dashboard-page-header">
                <div className="dashboard-page-heading">
                    <h1 className="page__title" style={{ fontSize: '2.5rem', fontWeight: 900, letterSpacing: '-0.04em', marginBottom: '8px', color: '#1a1d2e' }}>Maintenance</h1>
                    <p className="page__subtitle" style={{ fontSize: '1.125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>{openTicketsCount} active tickets require your attention</p>
                </div>
                <div className="dashboard-page-actions">
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ padding: '0.875rem 1.25rem', borderRadius: '14px', border: '1px solid #e2e8f0', fontSize: '0.9375rem', fontWeight: 600, background: '#fff', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <option value="all">All Statuses</option>
                        <option value="new">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                    </select>
                    <button
                        className="btn btn-primary"
                        onClick={() => { setEditingTicket(null); setTitle(''); setDescription(''); setPropertyId(''); setPriority('medium'); setStatus('new'); setIsAddModalOpen(true); }}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '14px', padding: '0.875rem 1.75rem', fontWeight: 700, background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', border: 'none', boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.3)' }}
                    >
                        <Plus size={20} /> New Ticket
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '10rem 0' }}>
                    <Loader2 className="animate-spin" size={48} color="var(--color-primary)" />
                </div>
            ) : tickets.length === 0 ? (
                <EmptyState
                    icon={<Wrench />}
                    title="No maintenance requests"
                    description="Everything is running smoothly! Tenants haven't submitted any maintenance tickets yet."
                    actionLabel="Create Ticket"
                    onAction={() => setIsAddModalOpen(true)}
                />
            ) : (
                <div className="data-panel responsive-table maintenance-table-wrap" style={{ border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)', borderRadius: '24px', background: '#fff' }}>
                    <table className="data-table maintenance-table" style={{ background: '#fff', borderCollapse: 'separate', borderSpacing: 0, minWidth: '900px' }}>
                        <thead style={{ background: 'var(--color-bg-subtle)' }}>
                            <tr>
                                <th style={{ width: '36%', padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Ticket Title</th>
                                <th style={{ width: '20%', padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Property</th>
                                <th style={{ width: '13%', padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)', textAlign: 'left' }}>Priority</th>
                                <th style={{ width: '15%', padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)', textAlign: 'left' }}>Status</th>
                                <th style={{ width: '14%', padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)', textAlign: 'left' }}>Created Date</th>
                                <th style={{ width: '56px', padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTickets.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ padding: '4rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                                        No tickets match the selected filter.
                                    </td>
                                </tr>
                            )}
                            {filteredTickets.map(ticket => {
                                const property = properties.find(p => p.id === ticket.property_id);
                                return (
                                    <tr key={ticket.id} className={ticket.priority === 'high' ? 'priority-high' : ''} onClick={() => openDetailDrawer(ticket)} style={{ transition: 'background 0.2s', cursor: 'pointer' }}>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ fontWeight: 800, color: 'var(--color-text)', fontSize: '1rem', marginBottom: '6px', lineHeight: 1.3 }}>{ticket.title}</div>
                                            <div className="maintenance-table__description" style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', lineHeight: 1.55 }}>
                                                {ticket.description || 'No description provided.'}
                                            </div>
                                        </td>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                                                {property?.name || 'Unknown Property'}
                                            </div>
                                        </td>
                                        <td className="maintenance-table__meta-cell" style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <span className={`badge badge--${ticket.priority === 'high' ? 'red' : ticket.priority === 'medium' ? 'yellow' : 'gray'}`} style={{ fontSize: '0.75rem', padding: '6px 10px', fontWeight: 800, borderRadius: '8px', letterSpacing: '0.05em' }}>
                                                {ticket.priority.toUpperCase()}
                                            </span>
                                        </td>
                                                        <td className="maintenance-table__meta-cell" style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <span style={{ padding: '6px 12px', fontSize: '0.75rem', fontWeight: 800, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: ticket.status === 'resolved' ? 'rgba(34,197,94,0.1)' : (ticket.status === 'in_progress' ? 'rgba(245,158,11,0.1)' : 'rgba(22,163,74,0.08)'), color: ticket.status === 'resolved' ? '#16a34a' : (ticket.status === 'in_progress' ? '#d97706' : '#15803d'), textTransform: 'uppercase' as const, boxShadow: ticket.status === 'new' ? '0 0 0 1px rgba(22,163,74,0.25)' : 'none' }}>
                                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animation: ticket.status === 'new' ? 'pulse-dot 1.8s ease-in-out infinite' : 'none' }} />
                                                {ticket.status === 'new' ? 'Open' : ticket.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                                            </span>
                                        </td>
                                        <td className="maintenance-table__meta-cell" style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)', color: 'var(--color-text-tertiary)', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {formatDate(ticket.created_at)}
                                        </td>
                                        <td onClick={e => e.stopPropagation()} style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)', textAlign: 'right' }}>
                                            <button
                                                onClick={(e) => handleOpenMenu(e, ticket.id)}
                                                style={{ background: openMenuTicketId === ticket.id ? 'var(--color-bg-subtle)' : 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)', padding: '8px', borderRadius: '50%' }}
                                            >
                                                <MoreVertical size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Portal dropdown — rendered at document.body, never clipped */}
            {activeTicket && openMenuTicketId && (
                <TicketMenu
                    ticket={activeTicket}
                    pos={menuPos}
                    onEdit={() => openEditModal(activeTicket)}
                    onStatus={handleStatusChange}
                    onDelete={handleDeleteTicket}
                    onClose={() => setOpenMenuTicketId(null)}
                />
            )}

            {/* New / Edit Modal */}
            {isAddModalOpen && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => { setIsAddModalOpen(false); setEditingTicket(null); setStatus('new'); }}
                    style={{ background: 'rgba(15, 23, 42, 0.65)' }}
                >
                    <div
                        className="dashboard-modal"
                        style={{ background: '#fff', padding: '3.5rem', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)', animation: 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '3rem' }}>
                            <div>
                                <div style={{ display: 'inline-flex', padding: '10px', borderRadius: '14px', background: 'rgba(22,163,74,0.08)', color: '#16a34a', marginBottom: '1rem' }}>
                                    <Wrench size={24} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#1a1d2e' }}>{editingTicket ? 'Edit Ticket' : 'New Ticket'}</h3>
                                <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', marginTop: '8px', fontWeight: 500 }}>{editingTicket ? 'Update maintenance details and priority' : 'Log a new maintenance issue for your property'}</p>
                            </div>
                            <button onClick={() => { setIsAddModalOpen(false); setEditingTicket(null); setStatus('new'); }} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateOrUpdateTicket} style={{ gap: '2.5rem', display: 'flex', flexDirection: 'column' }}>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issue Summary</label>
                                <input type="text" placeholder="e.g. Leaking kitchen faucet" value={title} onChange={e => setTitle(e.target.value)} required style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none' }} />
                            </div>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Detailed Description</label>
                                <textarea placeholder="Provide more details for the handyman..." value={description} onChange={e => setDescription(e.target.value)} style={{ width: '100%', minHeight: '160px', padding: '1.25rem', borderRadius: '16px', border: '1px solid #e2e8f0', fontFamily: 'inherit', fontSize: '1.0625rem', fontWeight: 500, resize: 'none', background: '#f8fafc', lineHeight: '1.6', color: '#1a1d2e', outline: 'none' }} />
                            </div>
                            <div className="dashboard-form-grid">
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Property</label>
                                    <select value={propertyId} onChange={e => setPropertyId(e.target.value)} required style={{ width: '100%', padding: '1.25rem', borderRadius: '16px', border: '1px solid #e2e8f0', fontSize: '1.0625rem', background: '#f8fafc', fontWeight: 600, cursor: 'pointer', appearance: 'none' }}>
                                        <option value="">Select property</option>
                                        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Priority Level</label>
                                    <select value={priority} onChange={e => setPriority(e.target.value as any)} style={{ width: '100%', padding: '1.25rem', borderRadius: '16px', border: '1px solid #e2e8f0', fontSize: '1.0625rem', background: '#f8fafc', fontWeight: 600, cursor: 'pointer', appearance: 'none' }}>
                                        <option value="low">Low Priority</option>
                                        <option value="medium">Medium Priority</option>
                                        <option value="high">High Priority</option>
                                    </select>
                                </div>
                            </div>

                            {editingTicket && (
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
                                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                        {([
                                            { value: 'new', label: '🔵 Open', color: '#2563eb', bg: 'rgba(59,130,246,0.08)', activeBg: 'rgba(59,130,246,0.15)', border: '#2563eb' },
                                            { value: 'in_progress', label: '🟡 In Progress', color: '#d97706', bg: 'rgba(245,158,11,0.08)', activeBg: 'rgba(245,158,11,0.15)', border: '#d97706' },
                                            { value: 'resolved', label: '🟢 Resolved', color: '#16a34a', bg: 'rgba(34,197,94,0.08)', activeBg: 'rgba(34,197,94,0.15)', border: '#16a34a' },
                                        ] as const).map(opt => (
                                            <button key={opt.value} type="button" onClick={() => setStatus(opt.value)} style={{ flex: 1, minWidth: 110, padding: '0.875rem 1rem', borderRadius: '14px', border: `2px solid ${status === opt.value ? opt.border : '#e2e8f0'}`, background: status === opt.value ? opt.activeBg : opt.bg, color: status === opt.value ? opt.color : '#64748b', fontWeight: status === opt.value ? 800 : 600, fontSize: '0.9375rem', cursor: 'pointer' }}>
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="dashboard-modal__actions" style={{ paddingTop: '2.5rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => { setIsAddModalOpen(false); setEditingTicket(null); setStatus('new'); }} style={{ padding: '1.125rem 2.25rem', borderRadius: '16px', fontWeight: 700, fontSize: '1rem' }}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting || !title || !propertyId} style={{ padding: '1.125rem 3.5rem', borderRadius: '16px', fontWeight: 800, fontSize: '1rem', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', boxShadow: '0 12px 24px -6px rgba(22,163,74,0.30)' }}>
                                    {isSubmitting ? (editingTicket ? 'Saving...' : 'Creating...') : (editingTicket ? 'Save Changes' : 'Create Ticket')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Ticket Detail Drawer ───────────────────────────────────────────── */}
            {detailTicket && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setDetailTicket(null)}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 8000, animation: 'fadeIn 0.2s ease-out' }}
                    />
                    {/* Drawer */}
                    <div style={{
                        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 92vw)',
                        background: '#fff', zIndex: 8001, boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
                        display: 'flex', flexDirection: 'column', animation: 'drawerSlideIn 0.28s cubic-bezier(0.16,1,0.3,1)',
                        overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <div style={{ padding: '1.75rem 2rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                            <div style={{ flex: 1, marginRight: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ padding: '6px 12px', fontSize: '0.75rem', fontWeight: 800, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: detailTicket.status === 'resolved' ? 'rgba(34,197,94,0.1)' : detailTicket.status === 'in_progress' ? 'rgba(245,158,11,0.1)' : 'rgba(22,163,74,0.08)', color: detailTicket.status === 'resolved' ? '#16a34a' : detailTicket.status === 'in_progress' ? '#d97706' : '#15803d', textTransform: 'uppercase' as const }}>
                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animation: detailTicket.status === 'new' ? 'pulse-dot 1.8s ease-in-out infinite' : 'none' }} />
                                        {detailTicket.status === 'new' ? 'Open' : detailTicket.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                                    </span>
                                    <span className={`badge badge--${detailTicket.priority === 'high' ? 'red' : detailTicket.priority === 'medium' ? 'yellow' : 'gray'}`} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>
                                        {detailTicket.priority.toUpperCase()}
                                    </span>
                                </div>
                                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.02em', lineHeight: 1.3 }}>{detailTicket.title}</h2>
                                <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: '6px', fontWeight: 500 }}>
                                    {properties.find(p => p.id === detailTicket.property_id)?.name || 'Unknown Property'} · {formatDate(detailTicket.created_at)}
                                </div>
                            </div>
                            <button onClick={() => setDetailTicket(null)} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '8px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div style={{ flex: 1, overflow: 'auto', padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Description */}
                            <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '8px' }}>Description</div>
                                <p style={{ margin: 0, fontSize: '0.9375rem', color: '#334155', lineHeight: 1.65, background: '#f8fafc', padding: '1rem 1.25rem', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                    {detailTicket.description || 'No description provided.'}
                                </p>
                            </div>

                            {/* Photos */}
                            {ticketImages.length > 0 && (
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                        <ImageIcon size={15} color="#94a3b8" />
                                        <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: '#94a3b8' }}>
                                            Photos ({ticketImages.length})
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {ticketImages.map((img) => {
                                            const { data } = supabase.storage.from('ticket-images').getPublicUrl(img.storage_path);
                                            return (
                                                <img
                                                    key={img.id}
                                                    src={data.publicUrl}
                                                    alt="Ticket photo"
                                                    onClick={() => setLightboxSrc(data.publicUrl)}
                                                    style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0', cursor: 'pointer' }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Status change */}
                            <div>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '10px' }}>Change Status</div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {detailTicket.status !== 'new' && (
                                        <button onClick={async () => { await handleStatusChange(detailTicket.id, 'new'); setDetailTicket(t => t ? { ...t, status: 'new' } : t); }} style={{ flex: 1, padding: '0.625rem', borderRadius: '10px', border: '2px solid #3b82f6', background: 'rgba(59,130,246,0.06)', color: '#2563eb', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
                                            Reopen
                                        </button>
                                    )}
                                    {detailTicket.status !== 'in_progress' && (
                                        <button onClick={async () => { await handleStatusChange(detailTicket.id, 'in_progress'); setDetailTicket(t => t ? { ...t, status: 'in_progress' } : t); }} style={{ flex: 1, padding: '0.625rem', borderRadius: '10px', border: '2px solid #f59e0b', background: 'rgba(245,158,11,0.06)', color: '#d97706', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
                                            In Progress
                                        </button>
                                    )}
                                    {detailTicket.status !== 'resolved' && (
                                        <button onClick={async () => { await handleStatusChange(detailTicket.id, 'resolved'); setDetailTicket(t => t ? { ...t, status: 'resolved' } : t); }} style={{ flex: 1, padding: '0.625rem', borderRadius: '10px', border: '2px solid #16a34a', background: 'rgba(22,163,74,0.06)', color: '#16a34a', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
                                            ✓ Resolve
                                        </button>
                                    )}
                                    {detailTicket.status === 'resolved' && (
                                        <div style={{ flex: 1, padding: '0.625rem', borderRadius: '10px', background: 'rgba(22,163,74,0.08)', color: '#16a34a', fontWeight: 700, fontSize: '0.8125rem', textAlign: 'center' as const, border: '2px solid rgba(22,163,74,0.2)' }}>
                                            ✓ Resolved
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Comments */}
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                    <MessageSquare size={15} color="#94a3b8" />
                                    <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: '#94a3b8' }}>
                                        Comments ({comments.length})
                                    </div>
                                </div>
                                {loadingComments ? (
                                    <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 size={24} className="animate-spin" color="#94a3b8" /></div>
                                ) : comments.length === 0 ? (
                                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                        No comments yet. Be the first to leave a note.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {comments.map(c => (
                                            <div key={c.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #f1f5f9' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1a1d2e' }}>{c.author_name}</span>
                                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatDate(c.created_at)}</span>
                                                </div>
                                                <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569', lineHeight: 1.55 }}>{c.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Comment input */}
                        <form onSubmit={handleSubmitComment} style={{ padding: '1.25rem 2rem', borderTop: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', gap: '10px' }}>
                            <input
                                type="text"
                                value={commentText}
                                onChange={e => setCommentText(e.target.value)}
                                placeholder="Add a comment visible to the tenant..."
                                style={{ flex: 1, padding: '0.75rem 1rem', borderRadius: '12px', border: '1.5px solid #e8ecf0', background: '#f8fafc', fontSize: '0.9375rem', fontWeight: 500, color: '#1a1d2e', outline: 'none' }}
                            />
                            <button type="submit" disabled={!commentText.trim() || submittingComment} style={{ padding: '0.75rem 1rem', borderRadius: '12px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: !commentText.trim() ? 0.5 : 1 }}>
                                {submittingComment ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            </button>
                        </form>
                    </div>
                </>
            )}

            {/* Lightbox */}
            {lightboxSrc && (
                <div
                    onClick={() => setLightboxSrc(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', animation: 'fadeIn 0.2s ease-out' }}
                >
                    <img src={lightboxSrc} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12 }} />
                    <button onClick={() => setLightboxSrc(null)} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
                        <X size={20} />
                    </button>
                </div>
            )}

            <style>{`
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }
                @keyframes drawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .maintenance-table tbody tr:hover { background: rgba(22,163,74,0.025) !important; }
                .maintenance-table tbody tr.priority-high td:first-child { border-left: 3px solid #ef4444; }
            `}</style>
        </div>
    );
}
